import React, { useEffect } from 'react';
import { CargoOperationViewModel } from 'src/types/ViewModel/Stowage/CargoOperation/CargoOperationViewModel';
import {
    DefaultFlexStrechedWrapper,
    DefaultPanelWrapper,
    ElementWrapper,
    InputFieldsWrapper,
    PanelSectionContent,
    PanelSubHeader,
} from 'src/components/ui/odfjellPanel/OdfjellPanel.styles';
import OdfjellInputComponent from 'src/components/common/odfjell-input-component/OdfjellInputComponent';
import { InputChangeData } from 'src/components/common/odfjell-input-component/OdfjellInputComponent';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconButton } from 'src/styles/button.style';
import {
    getLitreWeightReferenceTemperature,
    getLitreWeight,
    getLitreWeightCorrectionFactor,
    getCorrectedLitreWeightValue,
    getCargoTemperatureValue,
    getMaxAdjacentTemperature,
    getLoadingMaxTemperature,
    getVoyageMinTemperature,
    getVoyageMaxTemperature,
    getUnloadingMaxTemperature,
    isCorrectedLitreWeightAdjusted,
    isCargoTemperatureAdjusted,
    getLoadingMinTemperature,
    getUnloadingMinTemperature,
} from '../utils/cargoOperationDisplayUtils';
import { roundTo6Decimals } from 'src/utils/numberHelper';
import { RotationCargo } from 'src/types/ViewModel/Stowage/Rotation/RotationCargo';
import { isNullOrUndefined } from 'src/utils/objectUtils';

interface PhysicalDataSectionProps {
    cargoOperationVM: CargoOperationViewModel;
    isFieldReadOnly: (field: string) => boolean;
    onProductInputChange: (changeObject: InputChangeData) => void;
    onCargoInputChange: (changeObject: InputChangeData) => void;
    isHeatingSectionVisible: boolean;
    toggleHeatingSectionVisible: () => void;
    onCorrectedLitreWeightChange: (value: number) => void;
}

export const PhysicalDataSection: React.FC<PhysicalDataSectionProps> = ({
    cargoOperationVM,
    isFieldReadOnly,
    onProductInputChange,
    onCargoInputChange,
    isHeatingSectionVisible,
    toggleHeatingSectionVisible,
    onCorrectedLitreWeightChange,
}) => {
    useEffect(() => {
        const correctedLitreWeight = getCorrectedLitreWeightValue(cargoOperationVM);
        if (correctedLitreWeight) {
            onCorrectedLitreWeightChange(correctedLitreWeight);
        }
    }, [
        cargoOperationVM.cargo.product?.litreWeight,
        cargoOperationVM.cargo.productChanges?.litreWeight,
        cargoOperationVM.cargo.product?.litreWeightCorrectionFactor,
        cargoOperationVM.cargo.productChanges?.litreWeightCorrectionFactor,
        cargoOperationVM.cargo.product?.litreWeightReferenceTemperature,
        cargoOperationVM.cargo.productChanges?.litreWeightReferenceTemperature,
        cargoOperationVM.cargo.temperature,
    ]);

    const getAdjustedCssClass = (field: string, property: string, cargo: RotationCargo): string => {
        if (!cargo?.productChanges) return '';
        if (!property && !isNullOrUndefined(cargo.productChanges[field]) && cargo.productChanges[field] !== cargo.product[field]) {
            return 'adjusted-input';
        } else if (
            property &&
            !isNullOrUndefined(cargo.productChanges[field]) &&
            !isNullOrUndefined(cargo.productChanges[field][property]) &&
            cargo.productChanges[field][property] !== cargo.product[field][property]
        ) {
            return 'adjusted-input';
        } else {
            return '';
        }
    };

    return (
        <PanelSectionContent>
            <PanelSubHeader>
                Physical data and heating requirements
                <IconButton className="grey-button" onClick={toggleHeatingSectionVisible}>
                    {isHeatingSectionVisible && <FontAwesomeIcon icon={'chevron-circle-down'} />}
                    {!isHeatingSectionVisible && <FontAwesomeIcon icon={'chevron-circle-up'} />}
                </IconButton>
            </PanelSubHeader>
            {!isHeatingSectionVisible && cargoOperationVM && (
                <DefaultFlexStrechedWrapper alignTop noMargin className="highlight-panel">
                    <DefaultPanelWrapper noMargin flexWidth="48%">
                        <ElementWrapper>
                            <InputFieldsWrapper gridColWidths="30% 18% 13% 28.5%">
                                <span>Litre weight at</span>
                                <span className={getAdjustedCssClass('litreWeightReferenceTemperature', null, cargoOperationVM.cargo)}>
                                    <OdfjellInputComponent
                                        isReadOnly={isFieldReadOnly('litreWeightReferenceTemperature')}
                                        type={'number'}
                                        value={getLitreWeightReferenceTemperature(cargoOperationVM)}
                                        name={'litreWeightReferenceTemperature'}
                                        dataObject={cargoOperationVM.cargo}
                                        leaveBlank={true}
                                        onChange={onProductInputChange}
                                        tabIndex={9}
                                        unit={'°C'}
                                        inputSize="small"
                                        maxDecimals={1}
                                    />
                                </span>
                                <span>
                                    <OdfjellInputComponent
                                        isReadOnly={true}
                                        type={'number'}
                                        value={cargoOperationVM.cargo.product?.litreWeight}
                                        disableUnit={true}
                                    />
                                </span>
                                <span className={getAdjustedCssClass('litreWeight', null, cargoOperationVM.cargo)}>
                                    <OdfjellInputComponent
                                        isReadOnly={isFieldReadOnly('litreWeight')}
                                        type={'number'}
                                        value={getLitreWeight(cargoOperationVM)}
                                        onChange={onProductInputChange}
                                        name={'litreWeight'}
                                        dataObject={cargoOperationVM.cargo}
                                        leaveBlank={true}
                                        tabIndex={10}
                                        maxDecimals={3}
                                        disableUnit={true}
                                    />
                                </span>
                            </InputFieldsWrapper>

                            <InputFieldsWrapper gridColWidths="55% 15% 20%">
                                <span>Correction factor / °C </span>
                                <span>
                                    <OdfjellInputComponent
                                        isReadOnly={true}
                                        type={'number'}
                                        value={cargoOperationVM.cargo.product?.litreWeightCorrectionFactor}
                                    />
                                </span>
                                <span className={getAdjustedCssClass('litreWeightCorrectionFactor', null, cargoOperationVM.cargo)}>
                                    <OdfjellInputComponent
                                        isReadOnly={isFieldReadOnly('litreWeightCorrectionFactor')}
                                        type={'number'}
                                        value={getLitreWeightCorrectionFactor(cargoOperationVM)}
                                        onChange={onProductInputChange}
                                        name={'litreWeightCorrectionFactor'}
                                        dataObject={cargoOperationVM.cargo}
                                        leaveBlank={true}
                                        tabIndex={11}
                                        maxDecimals={6}
                                        disableUnit={true}
                                    />
                                </span>
                            </InputFieldsWrapper>

                            <InputFieldsWrapper gridColWidths="55% 17% 20%">
                                <span className="first-input highlight-text">Corrected litre weight</span>
                                <span></span>
                                <span
                                    className={
                                        isCorrectedLitreWeightAdjusted(cargoOperationVM)
                                            ? 'adjusted-value input-small highlight-text'
                                            : 'input-small highlight-text'
                                    }
                                >
                                    <OdfjellInputComponent
                                        isReadOnly={true}
                                        type={'number'}
                                        value={roundTo6Decimals(getCorrectedLitreWeightValue(cargoOperationVM))}
                                        disableUnit={true}
                                    />
                                </span>
                            </InputFieldsWrapper>

                            <InputFieldsWrapper gridColWidths="55% 20% 25%">
                                <span className="first-input">Cargo temperature</span>
                                <span></span>
                                <span className={isCargoTemperatureAdjusted(cargoOperationVM) ? 'adjusted-input' : ''}>
                                    <OdfjellInputComponent
                                        isReadOnly={isFieldReadOnly('cargoTemperature')}
                                        type={'number'}
                                        value={getCargoTemperatureValue(cargoOperationVM)}
                                        onChange={onCargoInputChange}
                                        name={'cargoTemperature'}
                                        dataObject={cargoOperationVM.cargo}
                                        tabIndex={12}
                                        unit={'°C'}
                                        inputSize="small"
                                        maxDecimals={1}
                                    />
                                </span>
                            </InputFieldsWrapper>

                            <InputFieldsWrapper gridColWidths="55% 20% 25%">
                                <span className="first-input">Max adj. temp.</span>
                                <span></span>
                                <span className={cargoOperationVM.cargo.productChanges?.maxAdjacentTemperature ? 'adjusted-input' : ''}>
                                    <OdfjellInputComponent
                                        isReadOnly={isFieldReadOnly('maxAdjacentTemperature')}
                                        type={'number'}
                                        value={getMaxAdjacentTemperature(cargoOperationVM)}
                                        onChange={onProductInputChange}
                                        name={'maxAdjacentTemperature'}
                                        dataObject={cargoOperationVM.cargo}
                                        leaveBlank={true}
                                        tabIndex={13}
                                        unit={'°C'}
                                        inputSize="small"
                                        maxDecimals={1}
                                    />
                                </span>
                            </InputFieldsWrapper>
                        </ElementWrapper>
                    </DefaultPanelWrapper>

                    <DefaultPanelWrapper noMargin flexWidth="48%">
                        <InputFieldsWrapper gridColWidths={'40% 35% 25%'}>
                            <span>Loading temp. min/max</span>
                            <span>
                                <OdfjellInputComponent
                                    isReadOnly={true}
                                    type={'number'}
                                    value={getLoadingMinTemperature(cargoOperationVM)}
                                    unit={'°C'}
                                    inputSize="small"
                                />
                            </span>
                            <span className={getAdjustedCssClass('loadingTemperatureRange', 'max', cargoOperationVM.cargo)}>
                                <OdfjellInputComponent
                                    isReadOnly={isFieldReadOnly('loadingTemperatureRangeMax')}
                                    type={'number'}
                                    value={getLoadingMaxTemperature(cargoOperationVM)}
                                    onChange={onProductInputChange}
                                    name={'loadingTemperatureRangeMax'}
                                    dataObject={cargoOperationVM.cargo}
                                    leaveBlank={true}
                                    tabIndex={15}
                                    unit={'°C'}
                                    inputSize="small"
                                    maxDecimals={1}
                                />
                            </span>
                        </InputFieldsWrapper>

                        <InputFieldsWrapper gridColWidths={'40% 35% 25%'}>
                            <span>Voyage temp. min/max</span>
                            <span className={getAdjustedCssClass('voyageTemperatureRange', 'min', cargoOperationVM.cargo)}>
                                <OdfjellInputComponent
                                    isReadOnly={isFieldReadOnly('voyageTemperatureRangeMin')}
                                    type={'number'}
                                    value={getVoyageMinTemperature(cargoOperationVM)}
                                    onChange={onProductInputChange}
                                    name={'voyageTemperatureRangeMin'}
                                    dataObject={cargoOperationVM.cargo}
                                    leaveBlank={true}
                                    unit={'°C'}
                                    inputSize="small"
                                    tabIndex={15}
                                    maxDecimals={1}
                                />
                            </span>
                            <span className={getAdjustedCssClass('voyageTemperatureRange', 'max', cargoOperationVM.cargo)}>
                                <OdfjellInputComponent
                                    isReadOnly={isFieldReadOnly('voyageTemperatureRangeMax')}
                                    type={'number'}
                                    value={getVoyageMaxTemperature(cargoOperationVM)}
                                    onChange={onProductInputChange}
                                    name={'voyageTemperatureRangeMax'}
                                    dataObject={cargoOperationVM.cargo}
                                    leaveBlank={true}
                                    unit={'°C'}
                                    inputSize="small"
                                    tabIndex={16}
                                    maxDecimals={1}
                                />
                            </span>
                        </InputFieldsWrapper>

                        <InputFieldsWrapper gridColWidths={'40% 35% 25%'}>
                            <span>Unloading temp. min/max</span>
                            <span>
                                <OdfjellInputComponent
                                    isReadOnly={true}
                                    type={'number'}
                                    value={getUnloadingMinTemperature(cargoOperationVM)}
                                    onChange={onProductInputChange}
                                    unit={'°C'}
                                    inputSize="small"
                                />
                            </span>
                            <span className={getAdjustedCssClass('unloadingTemperatureRange', 'max', cargoOperationVM.cargo)}>
                                <OdfjellInputComponent
                                    isReadOnly={isFieldReadOnly('unloadingTemperatureRangeMax')}
                                    type={'number'}
                                    value={getUnloadingMaxTemperature(cargoOperationVM)}
                                    onChange={onProductInputChange}
                                    name={'unloadingTemperatureRangeMax'}
                                    dataObject={cargoOperationVM.cargo}
                                    leaveBlank={true}
                                    tabIndex={17}
                                    unit={'°C'}
                                    inputSize="small"
                                    maxDecimals={1}
                                />
                            </span>
                        </InputFieldsWrapper>

                        <InputFieldsWrapper gridColWidths={'50% 25% 25%'}>
                            <span>Melting point (from/to)</span>
                            <span>
                                <OdfjellInputComponent
                                    isReadOnly={true}
                                    type={'number'}
                                    value={cargoOperationVM.cargo.product?.meltingPointRange?.min ?? 0}
                                    unit={'°C'}
                                    inputSize="small"
                                />
                            </span>
                            <span>
                                <OdfjellInputComponent
                                    isReadOnly={true}
                                    type={'number'}
                                    value={cargoOperationVM.cargo.product?.meltingPointRange?.max ?? 0}
                                    unit={'°C'}
                                    inputSize="small"
                                />
                            </span>
                        </InputFieldsWrapper>

                        <InputFieldsWrapper gridColWidths={'50% 25% 25%'}>
                            <span>Boiling point (from/to)</span>
                            <span>
                                <OdfjellInputComponent
                                    isReadOnly={true}
                                    type={'number'}
                                    value={cargoOperationVM.cargo.product?.boilingPointRange?.min ?? 0}
                                    unit={'°C'}
                                    inputSize="small"
                                />
                            </span>
                            <span>
                                <OdfjellInputComponent
                                    isReadOnly={true}
                                    type={'number'}
                                    value={cargoOperationVM.cargo.product?.boilingPointRange?.max ?? 0}
                                    unit={'°C'}
                                    inputSize="small"
                                />
                            </span>
                        </InputFieldsWrapper>

                        <InputFieldsWrapper gridColWidths={'50% 25% 25%'}>
                            <span>Flash point (from/to)</span>
                            <span>
                                <OdfjellInputComponent
                                    isReadOnly={true}
                                    type={'number'}
                                    value={cargoOperationVM.cargo.product?.flashPointRange?.min ?? 0}
                                    unit={'°C'}
                                    inputSize="small"
                                />
                            </span>
                            <span>
                                <OdfjellInputComponent
                                    isReadOnly={true}
                                    type={'number'}
                                    value={cargoOperationVM.cargo.product?.flashPointRange?.max ?? 0}
                                    unit={'°C'}
                                    inputSize="small"
                                />
                            </span>
                        </InputFieldsWrapper>
                    </DefaultPanelWrapper>
                </DefaultFlexStrechedWrapper>
            )}
        </PanelSectionContent>
    );
};
